import { useEffect, useState } from 'react'
import { audioSystem } from '../pc/feel/audio'
import {
  useArenaStore,
  type InteractionResponseFailure,
} from '../store'
import type { InteractionAnswer } from '../api/types'
import { INPUT_PRIORITY, modalInputRouter } from '../input/modalInputRouter'

/**
 * In-game interaction dialog. Opens when the player presses [E] near a waiting agent.
 * Supports three answer forms: approval (allow/deny), structured (option list), free-text.
 * Pending state clears only after acceptance, or an authoritative conflict refresh.
 */
export function InteractionDialog() {
  const activeInteraction    = useArenaStore((s) => s.activeInteraction)
  const setActiveInteraction = useArenaStore((s) => s.setActiveInteraction)
  const submitResponse       = useArenaStore((s) => s.submitInteractionResponse)

  const [freeText, setFreeText]       = useState('')
  const [selected, setSelected]       = useState<Record<string, string[]>>({})
  const [submitting, setSubmitting]   = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Reset local state when a new interaction opens
  useEffect(() => {
    if (activeInteraction) {
      setFreeText('')
      setSelected({})
      setSubmitting(false)
      setSubmitError(null)
    }
  }, [activeInteraction?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Modal input owns gameplay keys while the dialog is open.
  useEffect(() => {
    if (!activeInteraction) return
    return modalInputRouter.register(INPUT_PRIORITY.interaction, (e) => {
      if (e.key === 'Escape') {
        if (!submitting) close()
        return true
      }
      return e.code === 'KeyE' || e.code === 'KeyM' || e.key === 'Tab'
    })
  }, [activeInteraction, submitting]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!activeInteraction) return null

  const interaction = activeInteraction
  const isApproval    = interaction.type === 'approval'
  const hasStructured = (interaction.questions?.length ?? 0) > 0

  function close() {
    if (submitting) return
    audioSystem.playUIClick()
    setActiveInteraction(null)
  }

  async function submit(answer: InteractionAnswer) {
    if (submitting) return
    setSubmitting(true)
    setSubmitError(null)

    const result = await submitResponse(interaction, answer)
    if (useArenaStore.getState().activeInteraction?.id !== interaction.id) return

    if (result.status === 'accepted' || result.status === 'already-answered') {
      setSubmitting(false)
      setActiveInteraction(null)
      return
    }

    setSubmitting(false)
    setSubmitError(responseFailureMessage(result.reason))
  }

  function handleApprove(allow: boolean) {
    audioSystem.playUIClick()
    void submit({ kind: 'approval', allow })
  }

  function handleStructuredSubmit() {
    audioSystem.playUIClick()
    const questions = interaction.questions ?? []
    const selects = questions
      .filter((q) => q.question)
      .map((q) => ({
        question: q.question!,
        labels: selected[q.question!] ?? [],
      }))
    void submit({ kind: 'structured', selects })
  }

  function handleFreeTextSubmit() {
    const text = freeText.trim()
    if (!text) return
    audioSystem.playUIClick()
    void submit({ kind: 'text', text })
  }

  function toggleOption(question: string, label: string, multiSelect: boolean) {
    setSelected((prev) => {
      const current = prev[question] ?? []
      if (multiSelect) {
        return {
          ...prev,
          [question]: current.includes(label)
            ? current.filter((l) => l !== label)
            : [...current, label],
        }
      }
      return { ...prev, [question]: [label] }
    })
  }

  const structuredComplete =
    hasStructured &&
    (interaction.questions ?? [])
      .filter((q) => q.question)
      .every((q) => (selected[q.question!] ?? []).length > 0)

  return (
    <div
      style={s.overlay}
      onClick={(e) => { if (!submitting && e.target === e.currentTarget) close() }}
    >
      <div style={s.panel}>
        {/* Header */}
        <div style={s.header}>
          <span style={s.badge}>
            {isApproval ? 'Approval Request' : 'Agent Question'}
          </span>
          {interaction.agentId && (
            <span style={s.agentId}>{interaction.agentId}</span>
          )}
          <button
            style={{ ...s.closeBtn, ...(submitting ? s.disabledBtn : {}) }}
            onClick={close}
            aria-label="Close"
            disabled={submitting}
          >
            ✕
          </button>
        </div>

        {/* Approval: show action text, tool name, and arguments */}
        {isApproval && (
          <div style={s.approvalBody}>
            <p style={s.prompt}>{interaction.prompt}</p>
            <div style={s.toolRow}>
              <span style={s.toolLabel}>Tool</span>
              <span style={s.toolName}>{interaction.toolName ?? 'unknown'}</span>
            </div>
            {interaction.toolArguments && (
              <pre style={s.argsBlock}>
                {JSON.stringify(interaction.toolArguments, null, 2)}
              </pre>
            )}
          </div>
        )}

        {/* Question: prompt text */}
        {!isApproval && (
          <p style={s.prompt}>{interaction.prompt}</p>
        )}

        {/* Structured options */}
        {hasStructured && (
          <div style={s.questions}>
            {(interaction.questions ?? []).map((q, qi) => (
              <div key={qi} style={s.question}>
                {q.header && <div style={s.questionHeader}>{q.header}</div>}
                {q.question && q.question !== interaction.prompt && (
                  <div style={s.questionText}>{q.question}</div>
                )}
                <div style={s.options}>
                  {(q.options ?? []).map((opt) => {
                    const isSelected = (selected[q.question ?? ''] ?? []).includes(opt.label)
                    return (
                      <button
                        key={opt.label}
                        style={{
                          ...s.optionBtn,
                          ...(isSelected ? s.optionBtnSelected : {}),
                          ...(submitting ? s.disabledBtn : {}),
                        }}
                        onClick={() => toggleOption(q.question ?? '', opt.label, q.multi_select ?? false)}
                        disabled={submitting}
                      >
                        <span style={s.optionCheck}>{isSelected ? '✓' : ' '}</span>
                        <span style={s.optionLabel}>{opt.label}</span>
                        {opt.description && (
                          <span style={s.optionDesc}>{opt.description}</span>
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Free-text input (bare question with no structured options) */}
        {!isApproval && !hasStructured && (
          <textarea
            style={s.textarea}
            placeholder="Type your answer…"
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            disabled={submitting}
            autoFocus
            rows={3}
          />
        )}

        {submitting && (
          <div role="status" aria-live="polite" style={s.submitStatus}>
            Submitting response…
          </div>
        )}
        {submitError && (
          <div role="alert" style={s.submitError}>
            {submitError}
          </div>
        )}

        {/* Action buttons */}
        <div style={s.actions}>
          {isApproval ? (
            <>
              <button
                style={{ ...s.actionBtn, ...s.denyBtn, ...(submitting ? s.disabledBtn : {}) }}
                onClick={() => handleApprove(false)}
                disabled={submitting}
              >
                Deny
              </button>
              <button
                style={{ ...s.actionBtn, ...s.allowBtn, ...(submitting ? s.disabledBtn : {}) }}
                onClick={() => handleApprove(true)}
                disabled={submitting}
              >
                Allow
              </button>
            </>
          ) : hasStructured ? (
            <>
              <button
                style={{ ...s.actionBtn, ...(submitting ? s.disabledBtn : {}) }}
                onClick={close}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                style={{
                  ...s.actionBtn,
                  ...s.allowBtn,
                  ...(!structuredComplete || submitting ? s.disabledBtn : {}),
                }}
                onClick={handleStructuredSubmit}
                disabled={!structuredComplete || submitting}
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </>
          ) : (
            <>
              <button
                style={{ ...s.actionBtn, ...(submitting ? s.disabledBtn : {}) }}
                onClick={close}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                style={{
                  ...s.actionBtn,
                  ...s.allowBtn,
                  ...(!freeText.trim() || submitting ? s.disabledBtn : {}),
                }}
                onClick={handleFreeTextSubmit}
                disabled={!freeText.trim() || submitting}
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function responseFailureMessage(reason: InteractionResponseFailure): string {
  switch (reason) {
    case 'not-authorized':
      return 'Response was not accepted. You may no longer be authorized to answer this interaction.'
    case 'conflict':
      return 'The interaction changed before your response was accepted. Refresh and try again.'
    case 'session-changed':
      return 'Your session changed before the response was confirmed. The interaction remains pending.'
    case 'unavailable':
      return 'Response could not be sent. The interaction remains pending; try again.'
  }
}

const s: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.7)',
    backdropFilter: 'blur(6px)',
    zIndex: 50,
  },
  panel: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    background: 'rgba(61,64,91,0.96)',
    border: '1px solid rgba(245,235,224,0.15)',
    borderRadius: '1rem',
    padding: '1.5rem',
    minWidth: '22rem',
    maxWidth: '38rem',
    width: '90vw',
    boxShadow: '0 8px 40px rgba(0,0,0,0.55)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.2rem 0.6rem',
    borderRadius: '9999px',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    background: 'rgba(224,122,95,0.18)',
    border: '1px solid rgba(224,122,95,0.4)',
    color: '#e07a5f',
    flexShrink: 0,
  },
  agentId: {
    flex: 1,
    fontSize: '0.75rem',
    color: 'rgba(245,235,224,0.4)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(245,235,224,0.35)',
    cursor: 'pointer',
    fontSize: '1rem',
    padding: '0 0.25rem',
    flexShrink: 0,
  },
  approvalBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.625rem',
  },
  toolRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  toolLabel: {
    fontSize: '0.7rem',
    color: 'rgba(245,235,224,0.4)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontWeight: 700,
    flexShrink: 0,
  },
  toolName: {
    fontSize: '0.9rem',
    color: '#a78bfa',
    fontWeight: 700,
    fontFamily: 'var(--font-mono)',
  },
  argsBlock: {
    background: 'rgba(0,0,0,0.35)',
    border: '1px solid rgba(176,137,104,0.2)',
    borderRadius: '0.5rem',
    padding: '0.75rem',
    margin: 0,
    fontSize: '0.78rem',
    color: '#f5ebe0',
    fontFamily: 'var(--font-mono)',
    overflowX: 'auto' as const,
    maxHeight: '10rem',
    overflowY: 'auto' as const,
  },
  prompt: {
    fontSize: '0.925rem',
    color: '#f5ebe0',
    lineHeight: 1.6,
    margin: 0,
    fontWeight: 400,
  },
  questions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  question: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
  },
  questionHeader: {
    fontSize: '0.7rem',
    color: 'rgba(245,235,224,0.4)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontWeight: 700,
  },
  questionText: {
    fontSize: '0.875rem',
    color: 'rgba(245,235,224,0.75)',
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
  },
  optionBtn: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    background: 'rgba(0,0,0,0.2)',
    border: '1px solid rgba(245,235,224,0.1)',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    textAlign: 'left' as const,
    width: '100%',
    transition: 'border-color 0.12s',
  },
  optionBtnSelected: {
    border: '1px solid rgba(224,122,95,0.5)',
    background: 'rgba(224,122,95,0.08)',
  },
  optionCheck: {
    fontSize: '0.8rem',
    color: '#e07a5f',
    width: '1rem',
    flexShrink: 0,
    fontFamily: 'var(--font-mono)',
  },
  optionLabel: {
    fontSize: '0.875rem',
    color: '#f5ebe0',
    fontWeight: 600,
  },
  optionDesc: {
    fontSize: '0.75rem',
    color: 'rgba(245,235,224,0.4)',
    marginLeft: 'auto',
    paddingLeft: '0.5rem',
  },
  textarea: {
    width: '100%',
    padding: '0.625rem 0.75rem',
    background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(176,137,104,0.3)',
    borderRadius: '0.5rem',
    color: '#f5ebe0',
    fontSize: '0.875rem',
    fontFamily: 'var(--font-sans)',
    resize: 'vertical' as const,
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  submitStatus: {
    color: '#a5f3fc',
    fontSize: '0.8rem',
  },
  submitError: {
    color: '#fecaca',
    background: 'rgba(127,29,29,0.35)',
    border: '1px solid rgba(248,113,113,0.45)',
    borderRadius: '0.5rem',
    padding: '0.65rem 0.75rem',
    fontSize: '0.8rem',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    paddingTop: '0.25rem',
  },
  actionBtn: {
    fontFamily: "'Nunito', system-ui, sans-serif",
    padding: '0.5rem 1.25rem',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: 700,
    cursor: 'pointer',
    border: '1px solid rgba(245,235,224,0.15)',
    background: 'rgba(0,0,0,0.2)',
    color: 'rgba(245,235,224,0.6)',
    transition: 'background 0.12s',
  },
  allowBtn: {
    background: 'rgba(163,177,138,0.18)',
    border: '1px solid rgba(163,177,138,0.4)',
    color: '#a3b18a',
  },
  denyBtn: {
    background: 'rgba(248,113,113,0.12)',
    border: '1px solid rgba(248,113,113,0.35)',
    color: '#f87171',
  },
  disabledBtn: {
    opacity: 0.45,
    cursor: 'not-allowed' as const,
  },
}
